// Tiny i18n layer: Finnish (default) + Swedish, with a {var} interpolating t().
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { setDateLocale } from './lib/dates'

export type Lang = 'fi' | 'sv'
export const LANGS: Lang[] = ['fi', 'sv']
const STORAGE_KEY = 'lang'

type Dict = Record<string, string>

const fi: Dict = {
  'app.tagline': 'Vuorosuunnittelu',
  'common.today': 'Tänään',
  'common.cancel': 'Peruuta',
  'common.saveChanges': 'Tallenna muutokset',
  'common.signOut': 'Kirjaudu ulos',
  'common.optional': 'valinnainen',
  'common.you': 'sinä',
  'common.close': 'Sulje',
  'common.back': 'Takaisin',
  'common.prev': 'Edellinen',
  'common.next': 'Seuraava',

  'login.title': 'Kirjaudu sisään',
  'login.subtitle': 'Varaa vuorosi tunti tunnilta ja pidä tiimi miehitettynä.',
  'login.tab.password': 'Salasana',
  'login.tab.link': 'Sähköpostilinkki',
  'login.email': 'Sähköposti',
  'login.password': 'Salasana',
  'login.passwordPlaceholder': 'Salasanasi',
  'login.signIn': 'Kirjaudu',
  'login.sendLink': 'Lähetä kirjautumislinkki',
  'login.noPasswordHint': 'Eikö salasanaa vielä ole? Siirry Sähköpostilinkki-välilehdelle luodaksesi sen.',
  'login.linkHint':
    'Ensimmäistä kertaa tai unohtuiko salasana? Lähetämme turvallisen linkin, jolla vahvistat osoitteesi ja asetat salasanan.',
  'login.enterEmailFirst': 'Syötä ensin sähköpostisi ja pyydä sitten linkki.',
  'login.checkInbox.title': 'Tarkista sähköpostisi',
  'login.checkInbox.body':
    'Lähetimme kirjautumislinkin osoitteeseen {email}. Avaa se tällä laitteella jatkaaksesi. Linkki vanhenee 30 minuutissa.',
  'login.devLink': 'Kehitystila — avaa kirjautumislinkki.',
  'login.back': 'Takaisin kirjautumiseen',
  'login.error.generic': 'Kirjautuminen epäonnistui.',
  'login.error.sendLink': 'Linkin lähettäminen epäonnistui.',

  'setpw.invalid.title': 'Linkki vanhentunut',
  'setpw.invalid.body': 'Tämä linkki on virheellinen tai jo käytetty. Pyydä uusi jatkaaksesi.',
  'setpw.title.new': 'Luo tilisi',
  'setpw.title.reset': 'Valitse uusi salasana',
  'setpw.sub.new': 'Vahvista tietosi ja valitse salasana osoitteelle {email}.',
  'setpw.sub.reset': 'Päivitä salasana osoitteelle {email}.',
  'setpw.name': 'Nimesi',
  'setpw.namePlaceholder': 'Etu- ja sukunimi',
  'setpw.password': 'Salasana',
  'setpw.passwordPlaceholder': 'Vähintään 8 merkkiä',
  'setpw.confirm': 'Vahvista salasana',
  'setpw.confirmPlaceholder': 'Kirjoita salasana uudelleen',
  'setpw.submit': 'Tallenna ja jatka',
  'setpw.error.length': 'Käytä vähintään 8 merkkiä.',
  'setpw.error.match': 'Salasanat eivät täsmää.',
  'setpw.error.generic': 'Salasanan asettaminen epäonnistui.',

  'nav.home': 'Etusivu',
  'nav.calendar': 'Vuorokalenteri',
  'nav.myShifts': 'Omat vuoroni',
  'nav.manage': 'Hallinta',
  'nav.approvals': 'Hyväksynnät',
  'nav.openTimes': 'Avaa tarpeet',
  'nav.team': 'Tiimi',
  'nav.openMenu': 'Avaa valikko',
  'greeting.morning': 'Hyvää huomenta',
  'greeting.afternoon': 'Hyvää iltapäivää',
  'greeting.evening': 'Hyvää iltaa',
  'topbar.hi': 'Hei, {name}',
  'topbar.welcome': 'Tervetuloa',
  'topbar.bookShift': 'Varaa vuoro',
  'user.addName': 'Lisää nimesi',
  'role.member': 'jäsen',
  'role.supervisor': 'esihenkilö',
  'role.admin': 'ylläpitäjä',

  'rag.green': 'Täysi miehitys',
  'rag.amber': 'Osittain miehitetty',
  'rag.red': 'Tarvitsee tekijöitä',
  'rag.none': 'Ei vuoroja',
  'rag.short.green': 'Miehitetty',
  'rag.short.amber': 'Osittain',
  'rag.short.red': 'Tarvitsee tekijöitä',
  'legend.covered': 'Miehitetty',
  'legend.partly': 'Osittain',
  'legend.needs': 'Tarvitsee tekijöitä',

  'cal.title': 'Vuorokalenteri',
  'cal.desc':
    'Selaa avoimia vuoroja ja varaa haluamasi tunnit. Värit näyttävät, kuinka hyvin kukin päivä on miehitetty.',
  'view.day': 'Päivä',
  'view.week': 'Viikko',
  'view.month': 'Kuukausi',
  'view.quarter': 'Neljännes',
  'view.year': 'Vuosi',
  'cal.noShifts': 'Ei vuoroja',
  'cal.hrs': '{covered}/{required} t',
  'cal.pending': '{n} odottaa',
  'cal.coveredPct': '{pct} % miehitetty',

  'day.fullDay': 'Koko päivä',
  'day.workingHours': 'Työajat',
  'day.personHours': '{covered}/{required} henkilötuntia miehitetty',
  'day.noDemand': 'Tälle päivälle ei ole avattu tarvetta',
  'day.filled': '{approved}/{required} täytetty',
  'day.openToBook': 'Vapaana varattavaksi',
  'day.booked': '{approved} varattu',
  'day.book': 'Varaa',
  'day.youreOn': 'Olet mukana',
  'day.peopleApprovals': 'Ihmiset ja hyväksynnät',
  'day.bookings': '{n} varausta',
  'day.noBookings': 'Ei vielä varauksia',
  'day.noBookingsHint': 'Kun ihmiset varaavat tunteja, ne näkyvät täällä hyväksyttäviksi.',
  'day.awaiting': 'ODOTTAA HYVÄKSYNTÄÄ',
  'day.confirmed': 'VAHVISTETTU',
  'day.approve': 'Hyväksy',
  'day.reject': 'Hylkää',
  'day.remove': 'Poista',

  'toast.booked': 'Vuoro pyydetty — odottaa hyväksyntää.',
  'toast.cancelled': 'Varaus peruutettu.',
  'toast.approved': 'Varaus hyväksytty.',
  'toast.rejected': 'Varaus hylätty.',
  'toast.loadDayErr': 'Päivän lataaminen epäonnistui.',
  'toast.bookErr': 'Varaaminen epäonnistui.',
  'toast.cancelErr': 'Peruutus epäonnistui.',
  'toast.updateErr': 'Päivitys epäonnistui.',

  'dash.title': 'Yleiskatsaus',
  'dash.subtitle': 'Näin seuraavat neljä viikkoa näyttävät.',
  'dash.subtitleName': 'Näin seuraavat neljä viikkoa näyttävät, {name}.',
  'dash.openDays': 'Avoimet päivät · 7 vrk',
  'dash.fullyCovered': 'Täysin miehitetty',
  'dash.needPeople': 'Tarvitsee yhä tekijöitä',
  'dash.awaitingApproval': 'Odottaa hyväksyntääsi',
  'dash.daysNeedPeople': 'Päivät jotka tarvitsevat tekijöitä',
  'dash.openCalendar': 'Avaa kalenteri',
  'dash.allCovered': 'Kaikki miehitetty 🎉',
  'dash.allCoveredHint': 'Jokainen avoin päivä seuraavan neljän viikon aikana on täysin miehitetty.',
  'dash.personHours': '{covered}/{required} henkilötuntia',
  'dash.upcomingShifts': 'Tulevat vuorosi',
  'dash.allShifts': 'Kaikki vuorot',
  'dash.noShiftsYet': 'Ei vielä varattuja vuoroja',
  'dash.noShiftsHint': 'Siirry kalenteriin ja varaa tunnit, jotka voit tehdä.',

  'my.title': 'Omat vuoroni',
  'my.subtitle': 'Kaikki varaamasi vuorot. Odottavat vuorot odottavat esihenkilön hyväksyntää.',
  'my.none.title': 'Ei tulevia vuoroja',
  'my.none.hint': 'Valitse kalenterista tunnit, jotka voit tehdä.',
  'my.showPast': 'Näytä menneet vuorot ({n} päivää)',
  'my.hidePast': 'Piilota menneet vuorot',
  'my.hours': '{n} tuntia',
  'my.loadErr': 'Vuorojesi lataaminen epäonnistui.',

  'team.title': 'Tiimi',
  'team.subtitle': 'Kaikki, jotka voivat kirjautua ja varata vuoroja.',
  'team.addPerson': 'Lisää henkilö',
  'team.col.person': 'Henkilö',
  'team.col.role': 'Rooli',
  'team.col.account': 'Tili',
  'team.col.actions': 'Toiminnot',
  'team.status.active': 'Aktiivinen',
  'team.status.invited': 'Kutsuttu',
  'team.status.disabled': 'Poistettu käytöstä',
  'team.enable': 'Ota käyttöön',
  'team.disable': 'Poista käytöstä',
  'team.empty': 'Ei vielä ketään',
  'team.modal.title': 'Lisää henkilö',
  'team.modal.sub': 'He asettavat oman salasanansa ensimmäisellä kirjautumiskerralla.',
  'team.modal.email': 'Sähköposti',
  'team.modal.name': 'Nimi',
  'team.modal.role': 'Rooli',
  'team.removeConfirm': 'Poistetaanko {name} tiimistä? Tämä poistaa myös heidän varauksensa.',
  'team.toast.roleUpdated': 'Rooli päivitetty.',
  'team.toast.accessRevoked': 'Käyttöoikeus poistettu.',
  'team.toast.accessRestored': 'Käyttöoikeus palautettu.',
  'team.toast.removed': 'Henkilö poistettu.',
  'team.toast.added': 'Henkilö lisätty — lähetimme heille kutsulinkin sähköpostitse.',
  'team.loadErr': 'Tiimin lataaminen epäonnistui.',
  'team.updateErr': 'Päivitys epäonnistui.',
  'team.addErr': 'Henkilön lisääminen epäonnistui.',

  'ot.title': 'Avaa tarpeet',
  'ot.subtitle':
    'Avaa tunnit, joina tarvitset tekijöitä. Nämä määrittävät punainen/keltainen/vihreä-miehityksen ja antavat tiimin varata.',
  'ot.newWindow': 'Avaa uusi ajanjakso',
  'ot.date': 'Päivä',
  'ot.from': 'Alkaen',
  'ot.to': 'Asti',
  'ot.perHour': 'Henkilöä / t',
  'ot.roleLabel': 'Rooli tai paikka',
  'ot.rolePlaceholder': 'esim. Vastaanotto, Keittiö',
  'ot.notes': 'Muistiinpanot',
  'ot.notesPlaceholder': 'Mitä tiimin tulisi tietää',
  'ot.openBtn': 'Avaa nämä ajat',
  'ot.empty.title': 'Ei vielä avattuja aikoja',
  'ot.empty.hint': 'Avaa ensimmäinen ajanjakso aloittaaksesi varausten keräämisen.',
  'ot.windows': '{n} ajanjaksoa',
  'ot.window1': '1 ajanjakso',
  'ot.perHourLabel': '{n} hlö/t',
  'ot.noLabel': 'Ei nimeä',
  'ot.edit': 'Muokkaa',
  'ot.edit.title': 'Muokkaa ajanjaksoa',
  'ot.endAfterStart': 'Lopetusajan on oltava aloitusajan jälkeen.',
  'ot.toast.opened': 'Ajat avattu.',
  'ot.toast.removed': 'Poistettu.',
  'ot.toast.updated': 'Päivitetty.',
  'ot.removeConfirm': 'Poistetaanko tämä tarve?',
  'ot.loadErr': 'Avattujen aikojen lataaminen epäonnistui.',
  'ot.openErr': 'Aikojen avaaminen epäonnistui.',
  'ot.removeErr': 'Poistaminen epäonnistui.',
  'ot.saveErr': 'Tallennus epäonnistui.',

  'ap.title': 'Hyväksynnät',
  'ap.subtitle': 'Vahvista tiimisi pyytämät alustavat vuorot.',
  'ap.tab.pending': 'Odottaa',
  'ap.tab.confirmed': 'Vahvistetut',
  'ap.none.pending.title': 'Ei hyväksyttävää',
  'ap.none.pending.hint': 'Olet ajan tasalla. Uudet pyynnöt näkyvät täällä.',
  'ap.none.confirmed.title': 'Ei vahvistettuja vuoroja',
  'ap.none.confirmed.hint': 'Hyväksytyt vuorot näkyvät täällä.',
  'ap.requests': '{n} pyyntöä',
  'ap.request1': '1 pyyntö',
  'ap.approve': 'Hyväksy',
  'ap.reject': 'Hylkää',
  'ap.remove': 'Poista',
  'ap.toast.approved': 'Hyväksytty.',
  'ap.toast.rejected': 'Hylätty.',
  'ap.revert': 'Palauta odottavaksi',
  'ap.toast.reverted': 'Palautettu odottavaksi.',
  'ap.loadErr': 'Varausten lataaminen epäonnistui.',

  'nav.profile': 'Profiili',

  'profile.title': 'Profiili',
  'profile.subtitle': 'Päivitä nimesi ja valitse esihenkilösi.',
  'profile.name': 'Nimesi',
  'profile.namePlaceholder': 'Etu- ja sukunimi',
  'profile.supervisor': 'Esihenkilösi',
  'profile.supervisorHint': 'Kun pyydät vuoron, esihenkilöllesi ilmoitetaan hyväksyntää varten.',
  'profile.noSupervisor': 'Ei esihenkilöä',
  'profile.save': 'Tallenna profiili',
  'profile.saved': 'Profiili tallennettu.',
  'profile.saveErr': 'Profiilin tallennus epäonnistui.',
  'profile.loadErr': 'Profiilin lataaminen epäonnistui.',

  'roles.title': 'Roolit',
  'roles.new': '+ Uusi rooli…',
  'roles.newPrompt': 'Uuden roolin nimi',
  'roles.all': 'Kaikki',
  'roles.none': 'Ei roolia',
  'roles.label': 'Rooli',
  'roles.createErr': 'Roolin luominen epäonnistui.',

  'ot.eventName': 'Tapahtuman nimi',
  'ot.eventNamePlaceholder': 'esim. Kesäjuhla',

  'day.whatsOn': 'Päivän ohjelma',
  'day.coworkers': 'Työkaverit',
  'day.offer': 'Tarjoa vuoroa',
  'day.withdraw': 'Peru tarjous',
  'day.take': 'Ota vuoro',
  'day.revert': 'Palauta',
  'day.offered': 'Tarjolla',
  'day.availableShifts': 'Tarjolla olevat vuorot',
  'day.chooseRole': 'Minkä roolin otat tähän aikaan?',
  'day.noRole': 'Ei roolia',

  'fab.add': 'Pikalisäys',
  'quick.book.sub': 'Pyydä vuoro itsellesi.',
  'quick.open.sub': 'Avaa aika, jona tarvitaan tekijöitä.',
  'quick.time': 'Aika',
  'quick.note': 'Muistiinpano',
  'quick.notePlaceholder': 'Vapaaehtoinen huomio',

  'my.offer': 'Tarjoa',
  'my.withdraw': 'Peru tarjous',
  'my.offered': 'Tarjolla',
  'my.available': 'Tarjolla olevat vuorot',
  'my.availableHint': 'Vuorot, joita työkaverit tarjoavat. Ota vuoro itsellesi.',
  'my.take': 'Ota vuoro',
  'my.noAvailable': 'Ei tarjolla olevia vuoroja juuri nyt.',

  'toast.offered': 'Vuoro tarjottu työkavereille.',
  'toast.withdrawn': 'Tarjous peruttu.',
  'toast.taken': 'Vuoro otettu.',
  'toast.reverted': 'Palautettu odottavaksi.',
  'toast.offerErr': 'Vuoron tarjoaminen epäonnistui.',
  'toast.takeErr': 'Vuoron ottaminen epäonnistui.',

  'status.approved': 'Hyväksytty',
  'status.pending': 'Odottaa',

  'confirm.title': 'Haluatko varmasti poistaa?',
  'confirm.body': 'Tätä ei voi perua.',
  'confirm.delete': 'Poista',

  'profile.language': 'Kieli',
  'profile.languageHint': 'Käyttöliittymän ja sähköpostien kieli.',
  'profile.password': 'Salasana',
  'profile.passwordNew': 'Uusi salasana',
  'profile.passwordConfirm': 'Vahvista salasana',
  'profile.passwordHint': 'Jätä tyhjäksi, jos et halua vaihtaa salasanaa.',
  'profile.changePassword': 'Vaihda salasana',
  'profile.pwTooShort': 'Käytä vähintään 8 merkkiä.',
  'profile.pwMismatch': 'Salasanat eivät täsmää.',

  'nav.balance': 'Tasapaino',
  'balance.title': 'Tasapaino',
  'balance.subtitle': 'Kuka on tehnyt kuinka monta vuoroa ja tuntia.',
  'balance.shifts': '{n} vuoroa',
  'balance.hours': '{n} tuntia',
  'balance.empty': 'Ei hyväksyttyjä vuoroja tällä ajanjaksolla.',
  'balance.range30': '30 pv',
  'balance.range90': '90 pv',
  'balance.range365': '365 pv',
  'balance.loadErr': 'Tilastojen lataaminen epäonnistui.',

  'mode.normal': 'Tavallinen',
  'mode.large': 'Suuri teksti',
  'mode.label': 'Näkymän koko',
  'mode.hint': 'Suuri tila käyttää isompaa tekstiä ja painikkeita sekä yksinkertaisempaa etusivua.',

  'simple.hello': 'Hei, {name}!',
  'simple.next': 'Seuraava vuorosi',
  'simple.noShifts': 'Ei tulevia vuoroja',
  'simple.book': 'Varaa vuoro',
  'simple.myShifts': 'Omat vuoroni',
  'simple.calendar': 'Kalenteri',

  'team.manage': 'Hallinnoi',
  'team.modal.password': 'Salasana',
  'team.modal.passwordHint':
    'Jos asetat salasanan, henkilö voi kirjautua heti sähköpostilla ja salasanalla — kutsulinkkiä ei lähetetä.',
  'team.modal.largeMode': 'Käytä suurta/yksinkertaista näkymää',
  'team.managePasswordHint': 'Aseta uusi salasana. Jätä tyhjäksi säilyttääksesi nykyisen.',
  'team.toast.saved': 'Tallennettu.',
  'team.toast.addedActive': 'Henkilö lisätty. He voivat kirjautua sähköpostilla ja salasanalla.',
}

const sv: Dict = {
  'app.tagline': 'Skiftplanering',
  'common.today': 'Idag',
  'common.cancel': 'Avbryt',
  'common.saveChanges': 'Spara ändringar',
  'common.signOut': 'Logga ut',
  'common.optional': 'valfritt',
  'common.you': 'du',
  'common.close': 'Stäng',
  'common.back': 'Tillbaka',
  'common.prev': 'Föregående',
  'common.next': 'Nästa',

  'login.title': 'Logga in',
  'login.subtitle': 'Boka dina skift timme för timme och håll teamet bemannat.',
  'login.tab.password': 'Lösenord',
  'login.tab.link': 'E‑postlänk',
  'login.email': 'E‑post',
  'login.password': 'Lösenord',
  'login.passwordPlaceholder': 'Ditt lösenord',
  'login.signIn': 'Logga in',
  'login.sendLink': 'Skicka inloggningslänk',
  'login.noPasswordHint': 'Inget lösenord än? Byt till fliken E‑postlänk för att skapa ett.',
  'login.linkHint':
    'Första gången eller glömt lösenordet? Vi mejlar en säker länk för att bekräfta din adress och välja ett lösenord.',
  'login.enterEmailFirst': 'Ange din e‑post först och begär sedan en länk.',
  'login.checkInbox.title': 'Kolla din inkorg',
  'login.checkInbox.body':
    'Vi skickade en inloggningslänk till {email}. Öppna den på den här enheten för att fortsätta. Länken går ut om 30 minuter.',
  'login.devLink': 'Utvecklarläge — öppna inloggningslänken.',
  'login.back': 'Tillbaka till inloggning',
  'login.error.generic': 'Det gick inte att logga in.',
  'login.error.sendLink': 'Det gick inte att skicka länken.',

  'setpw.invalid.title': 'Länken har gått ut',
  'setpw.invalid.body': 'Länken är ogiltig eller redan använd. Begär en ny för att fortsätta.',
  'setpw.title.new': 'Skapa ditt konto',
  'setpw.title.reset': 'Välj ett nytt lösenord',
  'setpw.sub.new': 'Bekräfta dina uppgifter och välj ett lösenord för {email}.',
  'setpw.sub.reset': 'Uppdatera lösenordet för {email}.',
  'setpw.name': 'Ditt namn',
  'setpw.namePlaceholder': 'För- och efternamn',
  'setpw.password': 'Lösenord',
  'setpw.passwordPlaceholder': 'Minst 8 tecken',
  'setpw.confirm': 'Bekräfta lösenord',
  'setpw.confirmPlaceholder': 'Ange lösenordet igen',
  'setpw.submit': 'Spara och fortsätt',
  'setpw.error.length': 'Använd minst 8 tecken.',
  'setpw.error.match': 'Lösenorden matchar inte.',
  'setpw.error.generic': 'Det gick inte att spara lösenordet.',

  'nav.home': 'Hem',
  'nav.calendar': 'Skiftkalender',
  'nav.myShifts': 'Mina skift',
  'nav.manage': 'Hantera',
  'nav.approvals': 'Godkännanden',
  'nav.openTimes': 'Öppna behov',
  'nav.team': 'Team',
  'nav.openMenu': 'Öppna meny',
  'greeting.morning': 'God morgon',
  'greeting.afternoon': 'God eftermiddag',
  'greeting.evening': 'God kväll',
  'topbar.hi': 'Hej, {name}',
  'topbar.welcome': 'Välkommen',
  'topbar.bookShift': 'Boka skift',
  'user.addName': 'Lägg till ditt namn',
  'role.member': 'medlem',
  'role.supervisor': 'arbetsledare',
  'role.admin': 'administratör',

  'rag.green': 'Fullt bemannad',
  'rag.amber': 'Delvis bemannad',
  'rag.red': 'Behöver folk',
  'rag.none': 'Inga skift',
  'rag.short.green': 'Bemannad',
  'rag.short.amber': 'Delvis',
  'rag.short.red': 'Behöver folk',
  'legend.covered': 'Bemannad',
  'legend.partly': 'Delvis',
  'legend.needs': 'Behöver folk',

  'cal.title': 'Skiftkalender',
  'cal.desc':
    'Bläddra bland öppna skift och boka de timmar du vill. Färgerna visar hur väl varje dag är bemannad.',
  'view.day': 'Dag',
  'view.week': 'Vecka',
  'view.month': 'Månad',
  'view.quarter': 'Kvartal',
  'view.year': 'År',
  'cal.noShifts': 'Inga skift',
  'cal.hrs': '{covered}/{required} h',
  'cal.pending': '{n} väntar',
  'cal.coveredPct': '{pct} % bemannad',

  'day.fullDay': 'Hela dagen',
  'day.workingHours': 'Arbetstider',
  'day.personHours': '{covered}/{required} persontimmar bemannade',
  'day.noDemand': 'Inget behov öppnat för denna dag',
  'day.filled': '{approved}/{required} fyllda',
  'day.openToBook': 'Ledigt att boka',
  'day.booked': '{approved} bokade',
  'day.book': 'Boka',
  'day.youreOn': 'Du är med',
  'day.peopleApprovals': 'Personer och godkännanden',
  'day.bookings': '{n} bokningar',
  'day.noBookings': 'Inga bokningar än',
  'day.noBookingsHint': 'När folk bokar timmar visas de här för godkännande.',
  'day.awaiting': 'VÄNTAR PÅ GODKÄNNANDE',
  'day.confirmed': 'BEKRÄFTADE',
  'day.approve': 'Godkänn',
  'day.reject': 'Avslå',
  'day.remove': 'Ta bort',

  'toast.booked': 'Skift begärt — väntar på godkännande.',
  'toast.cancelled': 'Bokning avbruten.',
  'toast.approved': 'Bokning godkänd.',
  'toast.rejected': 'Bokning avslagen.',
  'toast.loadDayErr': 'Det gick inte att ladda dagen.',
  'toast.bookErr': 'Det gick inte att boka.',
  'toast.cancelErr': 'Det gick inte att avbryta.',
  'toast.updateErr': 'Det gick inte att uppdatera.',

  'dash.title': 'Översikt',
  'dash.subtitle': 'Så här ser de kommande fyra veckorna ut.',
  'dash.subtitleName': 'Så här ser de kommande fyra veckorna ut, {name}.',
  'dash.openDays': 'Öppna dagar · 7 dgr',
  'dash.fullyCovered': 'Fullt bemannade',
  'dash.needPeople': 'Behöver fortfarande folk',
  'dash.awaitingApproval': 'Väntar på ditt godkännande',
  'dash.daysNeedPeople': 'Dagar som behöver folk',
  'dash.openCalendar': 'Öppna kalender',
  'dash.allCovered': 'Allt bemannat 🎉',
  'dash.allCoveredHint': 'Varje öppen dag de kommande fyra veckorna är fullt bemannad.',
  'dash.personHours': '{covered}/{required} persontimmar',
  'dash.upcomingShifts': 'Dina kommande skift',
  'dash.allShifts': 'Alla skift',
  'dash.noShiftsYet': 'Inga bokade skift än',
  'dash.noShiftsHint': 'Gå till kalendern och boka de timmar du kan jobba.',

  'my.title': 'Mina skift',
  'my.subtitle': 'Alla skift du bokat. Väntande skift inväntar en arbetsledares godkännande.',
  'my.none.title': 'Inga kommande skift',
  'my.none.hint': 'Välj de timmar du kan jobba i kalendern.',
  'my.showPast': 'Visa tidigare skift ({n} dagar)',
  'my.hidePast': 'Dölj tidigare skift',
  'my.hours': '{n} timmar',
  'my.loadErr': 'Det gick inte att ladda dina skift.',

  'team.title': 'Team',
  'team.subtitle': 'Alla som kan logga in och boka skift.',
  'team.addPerson': 'Lägg till person',
  'team.col.person': 'Person',
  'team.col.role': 'Roll',
  'team.col.account': 'Konto',
  'team.col.actions': 'Åtgärder',
  'team.status.active': 'Aktiv',
  'team.status.invited': 'Inbjuden',
  'team.status.disabled': 'Inaktiverad',
  'team.enable': 'Aktivera',
  'team.disable': 'Inaktivera',
  'team.empty': 'Ingen här än',
  'team.modal.title': 'Lägg till person',
  'team.modal.sub': 'De väljer sitt eget lösenord vid första inloggningen.',
  'team.modal.email': 'E‑post',
  'team.modal.name': 'Namn',
  'team.modal.role': 'Roll',
  'team.removeConfirm': 'Ta bort {name} från teamet? Detta tar även bort deras bokningar.',
  'team.toast.roleUpdated': 'Rollen uppdaterad.',
  'team.toast.accessRevoked': 'Åtkomst återkallad.',
  'team.toast.accessRestored': 'Åtkomst återställd.',
  'team.toast.removed': 'Person borttagen.',
  'team.toast.added': 'Person tillagd — vi mejlade en inbjudningslänk.',
  'team.loadErr': 'Det gick inte att ladda teamet.',
  'team.updateErr': 'Det gick inte att uppdatera.',
  'team.addErr': 'Det gick inte att lägga till personen.',

  'ot.title': 'Öppna behov',
  'ot.subtitle':
    'Öppna de timmar du behöver folk. Dessa styr röd/gul/grön‑bemanningen och låter teamet boka.',
  'ot.newWindow': 'Öppna ett nytt pass',
  'ot.date': 'Datum',
  'ot.from': 'Från',
  'ot.to': 'Till',
  'ot.perHour': 'Personer / h',
  'ot.roleLabel': 'Roll eller plats',
  'ot.rolePlaceholder': 't.ex. Reception, Kök',
  'ot.notes': 'Anteckningar',
  'ot.notesPlaceholder': 'Vad teamet bör veta',
  'ot.openBtn': 'Öppna dessa tider',
  'ot.empty.title': 'Inga öppnade tider än',
  'ot.empty.hint': 'Öppna ditt första pass för att börja ta emot bokningar.',
  'ot.windows': '{n} pass',
  'ot.window1': '1 pass',
  'ot.perHourLabel': '{n} pers/h',
  'ot.noLabel': 'Ingen etikett',
  'ot.edit': 'Redigera',
  'ot.edit.title': 'Redigera pass',
  'ot.endAfterStart': 'Sluttiden måste vara efter starttiden.',
  'ot.toast.opened': 'Tiderna öppnade.',
  'ot.toast.removed': 'Borttagen.',
  'ot.toast.updated': 'Uppdaterad.',
  'ot.removeConfirm': 'Ta bort detta behov?',
  'ot.loadErr': 'Det gick inte att ladda öppnade tider.',
  'ot.openErr': 'Det gick inte att öppna tiderna.',
  'ot.removeErr': 'Det gick inte att ta bort.',
  'ot.saveErr': 'Det gick inte att spara.',

  'ap.title': 'Godkännanden',
  'ap.subtitle': 'Bekräfta de preliminära skift ditt team begärt.',
  'ap.tab.pending': 'Väntar',
  'ap.tab.confirmed': 'Bekräftade',
  'ap.none.pending.title': 'Inget att godkänna',
  'ap.none.pending.hint': 'Du är ikapp. Nya förfrågningar visas här.',
  'ap.none.confirmed.title': 'Inga bekräftade skift',
  'ap.none.confirmed.hint': 'Godkända skift visas här.',
  'ap.requests': '{n} förfrågningar',
  'ap.request1': '1 förfrågan',
  'ap.approve': 'Godkänn',
  'ap.reject': 'Avslå',
  'ap.remove': 'Ta bort',
  'ap.toast.approved': 'Godkänd.',
  'ap.toast.rejected': 'Avslagen.',
  'ap.revert': 'Återställ till väntande',
  'ap.toast.reverted': 'Återställd till väntande.',
  'ap.loadErr': 'Det gick inte att ladda bokningar.',

  'nav.profile': 'Profil',

  'profile.title': 'Profil',
  'profile.subtitle': 'Uppdatera ditt namn och välj din arbetsledare.',
  'profile.name': 'Ditt namn',
  'profile.namePlaceholder': 'För- och efternamn',
  'profile.supervisor': 'Din arbetsledare',
  'profile.supervisorHint': 'När du begär ett skift meddelas din arbetsledare för godkännande.',
  'profile.noSupervisor': 'Ingen arbetsledare',
  'profile.save': 'Spara profil',
  'profile.saved': 'Profilen sparad.',
  'profile.saveErr': 'Det gick inte att spara profilen.',
  'profile.loadErr': 'Det gick inte att ladda profilen.',

  'roles.title': 'Roller',
  'roles.new': '+ Ny roll…',
  'roles.newPrompt': 'Namn på den nya rollen',
  'roles.all': 'Alla',
  'roles.none': 'Ingen roll',
  'roles.label': 'Roll',
  'roles.createErr': 'Det gick inte att skapa rollen.',

  'ot.eventName': 'Evenemangets namn',
  'ot.eventNamePlaceholder': 't.ex. Sommarfest',

  'day.whatsOn': 'Dagens program',
  'day.coworkers': 'Arbetskamrater',
  'day.offer': 'Erbjud skift',
  'day.withdraw': 'Dra tillbaka',
  'day.take': 'Ta skiftet',
  'day.revert': 'Återställ',
  'day.offered': 'Erbjuds',
  'day.availableShifts': 'Tillgängliga skift',
  'day.chooseRole': 'Vilken roll tar du då?',
  'day.noRole': 'Ingen roll',

  'fab.add': 'Snabbtillägg',
  'quick.book.sub': 'Begär ett skift åt dig själv.',
  'quick.open.sub': 'Öppna en tid då det behövs folk.',
  'quick.time': 'Tid',
  'quick.note': 'Anteckning',
  'quick.notePlaceholder': 'Valfri notering',

  'my.offer': 'Erbjud',
  'my.withdraw': 'Dra tillbaka',
  'my.offered': 'Erbjuds',
  'my.available': 'Tillgängliga skift',
  'my.availableHint': 'Skift som arbetskamrater erbjuder. Ta ett skift själv.',
  'my.take': 'Ta skiftet',
  'my.noAvailable': 'Inga tillgängliga skift just nu.',

  'toast.offered': 'Skiftet erbjuds till arbetskamrater.',
  'toast.withdrawn': 'Erbjudandet tillbakadraget.',
  'toast.taken': 'Skiftet taget.',
  'toast.reverted': 'Återställd till väntande.',
  'toast.offerErr': 'Det gick inte att erbjuda skiftet.',
  'toast.takeErr': 'Det gick inte att ta skiftet.',

  'status.approved': 'Godkänd',
  'status.pending': 'Väntar',

  'confirm.title': 'Vill du verkligen radera?',
  'confirm.body': 'Detta kan inte ångras.',
  'confirm.delete': 'Radera',

  'profile.language': 'Språk',
  'profile.languageHint': 'Språk för gränssnittet och e‑post.',
  'profile.password': 'Lösenord',
  'profile.passwordNew': 'Nytt lösenord',
  'profile.passwordConfirm': 'Bekräfta lösenord',
  'profile.passwordHint': 'Lämna tomt om du inte vill byta lösenord.',
  'profile.changePassword': 'Byt lösenord',
  'profile.pwTooShort': 'Använd minst 8 tecken.',
  'profile.pwMismatch': 'Lösenorden matchar inte.',

  'nav.balance': 'Balans',
  'balance.title': 'Balans',
  'balance.subtitle': 'Vem har gjort hur många skift och timmar.',
  'balance.shifts': '{n} skift',
  'balance.hours': '{n} timmar',
  'balance.empty': 'Inga godkända skift under denna period.',
  'balance.range30': '30 dgr',
  'balance.range90': '90 dgr',
  'balance.range365': '365 dgr',
  'balance.loadErr': 'Det gick inte att ladda statistiken.',

  'mode.normal': 'Vanlig',
  'mode.large': 'Stor text',
  'mode.label': 'Visningsstorlek',
  'mode.hint': 'Stort läge använder större text och knappar samt en enklare startsida.',

  'simple.hello': 'Hej, {name}!',
  'simple.next': 'Ditt nästa skift',
  'simple.noShifts': 'Inga kommande skift',
  'simple.book': 'Boka skift',
  'simple.myShifts': 'Mina skift',
  'simple.calendar': 'Kalender',

  'team.manage': 'Hantera',
  'team.modal.password': 'Lösenord',
  'team.modal.passwordHint':
    'Om du anger ett lösenord kan personen logga in direkt med e‑post och lösenord — ingen inbjudningslänk skickas.',
  'team.modal.largeMode': 'Använd stort/enkelt läge',
  'team.managePasswordHint': 'Ange ett nytt lösenord. Lämna tomt för att behålla nuvarande.',
  'team.toast.saved': 'Sparat.',
  'team.toast.addedActive': 'Person tillagd. De kan logga in med e‑post och lösenord.',
}

const DICTS: Record<Lang, Dict> = { fi, sv }

export type TFunc = (key: string, vars?: Record<string, string | number>) => string

interface I18nValue {
  lang: Lang
  setLang: (l: Lang) => void
  t: TFunc
}

const I18nContext = createContext<I18nValue | null>(null)

function readLang(): Lang {
  const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as Lang | null
  return stored && LANGS.includes(stored) ? stored : 'fi'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readLang())

  useEffect(() => {
    setDateLocale(lang === 'sv' ? 'sv-SE' : 'fi-FI')
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      /* ignore */
    }
  }, [])

  const t = useCallback<TFunc>(
    (key, vars) => {
      let str = DICTS[lang][key] ?? DICTS.fi[key] ?? key
      if (vars) for (const [k, v] of Object.entries(vars)) str = str.split(`{${k}}`).join(String(v))
      return str
    },
    [lang],
  )

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

export function LangToggle() {
  const { lang, setLang } = useI18n()
  return (
    <div className="segmented lang-toggle">
      {LANGS.map((l) => (
        <button key={l} type="button" className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  )
}
